import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  fetchMCPServersThunk,
  createMCPServerThunk,
  updateMCPServerThunk,
  deleteMCPServerThunk,
} from '@/redux/asyncThunks/mcpServer'
import { MCPServerForm, MCPServerFormData } from './MCPServerForm'
import { MCPServer } from '@/api/mcpServer'
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import { FileDown } from 'lucide-react'
import { toast } from 'sonner'
import OGEDeletionModal from '@/components/OGE/OGEDeletionModal'
import { ImportModal } from '@/components/Room/ImportModal'

interface MCPServerModalProps {
  isOpen: boolean
  onClose: () => void
  roomId: string
  roomName?: string
}

export function MCPServerModal({
  isOpen,
  onClose,
  roomId,
  roomName,
}: MCPServerModalProps) {
  const dispatch = useAppDispatch()
  const { servers, loading } = useAppSelector((state) => state.mcpServer)
  const [showForm, setShowForm] = useState(false)
  const [editingServer, setEditingServer] = useState<MCPServer | undefined>()
  const [deletingServerId, setDeletingServerId] = useState<number | null>(null)
  const [importMCP, setImportMCP] = useState<boolean>(false)

  useEffect(() => {
    if (isOpen && roomId) {
      dispatch(fetchMCPServersThunk(roomId))
    }
  }, [isOpen, roomId, dispatch])

  const handleCreateNew = () => {
    setEditingServer(undefined)
    setShowForm(true)
  }

  const handleEdit = (server: MCPServer) => {
    setEditingServer(server)
    setShowForm(true)
  }

  const handleDelete = async (serverId: number) => {
    setDeletingServerId(serverId)
  }

  const confirmDelete = async () => {
    if (deletingServerId === null) return

    try {
      await dispatch(
        deleteMCPServerThunk({ roomId, serverId: deletingServerId })
      ).unwrap()
      toast.success('MCP server deleted successfully')
      setDeletingServerId(null)
    } catch (error) {
      toast.error('Failed to delete MCP server')
    }
  }

  const handleSubmit = async (data: MCPServerFormData) => {
    try {
      // Parse JSON fields
      const args = JSON.parse(data.args)
      const env_vars = JSON.parse(data.env_vars)

      const payload = {
        name: data.name.trim(),
        description: data.description.trim() || undefined,
        command: data.command.trim(),
        args,
        env_vars: Object.keys(env_vars).length > 0 ? env_vars : undefined,
        enabled: data.enabled,
      }

      if (editingServer) {
        // Update existing server
        await dispatch(
          updateMCPServerThunk({
            roomId,
            serverId: editingServer.id,
            data: payload,
          })
        ).unwrap()
        toast.success('MCP server updated successfully')
      } else {
        // Create new server
        await dispatch(
          createMCPServerThunk({ roomId, data: payload })
        ).unwrap()
        toast.success('MCP server created successfully')
      }

      setShowForm(false)
      setEditingServer(undefined)
    } catch (error: any) {
      const errorMessage =
        error.message || 'Failed to save MCP server configuration'
      toast.error(errorMessage)
      throw error
    }
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingServer(undefined)
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className='max-w-4xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>MCP Server Configuration</DialogTitle>
            <DialogDescription>
              Manage Model Context Protocol (MCP) servers for this room.
              Configure servers that can be assigned to advisors.
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4'>
            {!showForm && (
              <>
                <div className='flex justify-between items-center'>
                  <h3 className='text-lg font-medium'>Available Servers</h3>
                  <div className='flex gap-2'>
                    <Button
                      type='button'
                      variant='outline'
                      onClick={() => setImportMCP(true)}
                    >
                      <FileDown className='w-4 h-4 mr-2' />
                      Import MCP
                    </Button>
                    <Button
                      onClick={handleCreateNew}
                      className='bg-dark-blue hover:bg-dark-blue-hovered'
                    >
                      <PlusIcon className='w-4 h-4 mr-2' />
                      Add Server
                    </Button>
                  </div>
                </div>

                {loading && servers.length === 0 ? (
                  <p className='text-center text-muted-foreground py-8'>
                    Loading servers...
                  </p>
                ) : servers.length === 0 ? (
                  <div className='text-center py-8'>
                    <p className='text-muted-foreground mb-4'>
                      No MCP servers configured yet.
                    </p>
                    <Button
                      onClick={handleCreateNew}
                      variant='outline'
                      className='mx-auto'
                    >
                      Create your first MCP server
                    </Button>
                  </div>
                ) : (
                  <div className='space-y-2'>
                    {servers.map((server) => (
                      <div
                        key={server.id}
                        className='flex items-center justify-between p-4 border rounded-lg hover:bg-accent'
                      >
                        <div className='flex-1'>
                          <div className='flex items-center gap-2'>
                            <h4 className='font-medium'>{server.name}</h4>
                            {!server.enabled && (
                              <span className='text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded'>
                                Disabled
                              </span>
                            )}
                          </div>
                          {server.description && (
                            <p className='text-sm text-muted-foreground mt-1'>
                              {server.description}
                            </p>
                          )}
                          <p className='text-xs text-muted-foreground mt-1'>
                            Command: {server.command} {server.args.join(' ')}
                          </p>
                        </div>
                        <div className='flex gap-2'>
                          <Button
                            variant='outline'
                            size='sm'
                            onClick={() => handleEdit(server)}
                          >
                            <PencilIcon className='w-4 h-4' />
                          </Button>
                          <Button
                            variant='outline'
                            size='sm'
                            onClick={() => handleDelete(server.id)}
                            className='text-red-600 hover:text-red-700'
                          >
                            <TrashIcon className='w-4 h-4' />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {showForm && (
              <div>
                <h3 className='text-lg font-medium mb-4'>
                  {editingServer ? 'Edit Server' : 'Create New Server'}
                </h3>
                <MCPServerForm
                  initialData={editingServer}
                  onSubmit={handleSubmit}
                  onCancel={handleCancel}
                  submitLabel={editingServer ? 'Update' : 'Create'}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <OGEDeletionModal
        isOpen={deletingServerId !== null}
        onClose={() => setDeletingServerId(null)}
        onConfirm={confirmDelete}
        title='Delete MCP Server'
        description='Are you sure you want to delete this MCP server? Advisors currently using this server will no longer have access to it.'
        confirmButtonText='Delete'
        cancelButtonText='Cancel'
      />

      {importMCP && (
        <ImportModal
          key={`${roomId}-mcp-import`}
          isOpen={importMCP}
          onClose={() => setImportMCP(false)}
          scope='advisors'
          roomId={roomId}
          roomName={roomName}
          mcpServersOnly
          onImportComplete={() => {
            setImportMCP(false)
            void dispatch(fetchMCPServersThunk(roomId))
          }}
        />
      )}
    </>
  )
}

